import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch, setApiAccessToken } from './apiClient';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { FALLBACK_BUSINESS_TYPES, filterSignupBusinessTypes } from './constants/businessTypes';
import { FALLBACK_LEGAL } from './constants/legalFallback';
import PublicAuthShell from './components/PublicAuthShell';
import { useLanguage } from './i18n/LanguageContext';
import { TANZANIA_REGIONS, getDistrictsByRegion } from './utils/tanzaniaLocations';
import './LoginPage.css';
import './RegisterPage.css';

const RegisterPage = ({
  onRegister,
  onShowLogin,
  onShowRegister,
  onGoHome,
  onShowTerms,
  onShowPrivacy,
  embedded = false,
  hideSwitch = false,
}) => {
  const { t } = useLanguage();
  const [companyName, setCompanyName] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [branchName, setBranchName] = useState('');
  const [branchRegion, setBranchRegion] = useState('');
  const [branchDistrict, setBranchDistrict] = useState('');
  const [branchStreet, setBranchStreet] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessTypes, setBusinessTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesLoadNote, setTypesLoadNote] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [regStep, setRegStep] = useState('form');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyNote, setVerifyNote] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [legal, setLegal] = useState(null);
  const [legalLoading, setLegalLoading] = useState(true);
  const [legalLoadNote, setLegalLoadNote] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLegalLoading(true);
      setLegalLoadNote('');
      try {
        const res = await apiFetch('/public/legal');
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data?.terms?.version && data?.privacy?.version) {
          setLegal(data);
          return;
        }
        throw new Error(data.message || 'Could not load legal documents from server.');
      } catch (e) {
        if (!cancelled) {
          setLegal(FALLBACK_LEGAL);
          setLegalLoadNote(
            e.message ||
              'Using offline Terms & Privacy — start the backend on port 8001 to register an account.',
          );
        }
      } finally {
        if (!cancelled) setLegalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTypesLoading(true);
      setTypesLoadNote('');
      try {
        const res = await apiFetch('/public/business-types');
        const bt = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          throw new Error(bt.message || 'Could not load business types from server.');
        }
        const raw = bt.businessTypes?.length ? bt.businessTypes : FALLBACK_BUSINESS_TYPES;
        const list = filterSignupBusinessTypes(raw);
        setBusinessTypes(list);
        if (list.length) {
          setBusinessType((prev) => (prev && list.some((t) => t.code === prev) ? prev : ''));
        }
        if (!bt.businessTypes?.length) {
          setTypesLoadNote('Using offline list — start the backend on port 8001 for the latest types.');
        }
      } catch (e) {
        if (!cancelled) {
          const list = filterSignupBusinessTypes(FALLBACK_BUSINESS_TYPES);
          setBusinessTypes(list);
          setBusinessType('');
          setTypesLoadNote(
            e.message || 'Server unavailable — showing default business types. Ensure backend runs on port 8001.',
          );
        }
      } finally {
        if (!cancelled) setTypesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const districtOptions = useMemo(
    () => (branchRegion ? getDistrictsByRegion(branchRegion) : []),
    [branchRegion]
  );

  const buildRegistrationBody = () => ({
    companyName: companyName.trim(),
    businessType,
    companyPhone: companyPhone.trim() || undefined,
    companyAddress: companyAddress.trim() || undefined,
    branchName: branchName.trim() || undefined,
    branchRegion,
    branchDistrict,
    branchStreet: branchStreet.trim() || undefined,
    ownerName: ownerName.trim(),
    email: email.trim(),
    password,
    acceptedTerms: true,
    termsVersion: legal.terms.version,
    privacyVersion: legal.privacy.version,
  });

  const validateRegistrationForm = () => {
    if (!companyName.trim() || !ownerName.trim() || !email.trim() || !password) {
      setError('Please enter business name, your name, email, and password.');
      return false;
    }
    if (!branchName.trim()) {
      setError('Please enter your branch or shop name.');
      return false;
    }
    if (!branchRegion) {
      setError('Please choose region (mkoa).');
      return false;
    }
    if (!branchDistrict) {
      setError('Please choose district (wilaya).');
      return false;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Password and confirmation do not match.');
      return false;
    }
    if (!businessTypes.length) {
      setError('Business types are still loading. Please wait a moment.');
      return false;
    }
    if (!businessType || !businessTypes.some((t) => t.code === businessType)) {
      setError('Please select your business type.');
      return false;
    }
    if (!acceptedTerms) {
      setError('You must accept the Terms of Service and Privacy Policy to continue.');
      return false;
    }
    if (!legal?.terms?.version || !legal?.privacy?.version) {
      setError('Legal documents are still loading. Please wait a moment and try again.');
      return false;
    }
    return true;
  };

  const handleSendVerification = async () => {
    setError('');
    setVerifyNote('');
    if (!validateRegistrationForm()) return;
    setLoading(true);
    try {
      const res = await apiFetch('/public/register/send-verification', {
        method: 'POST',
        body: JSON.stringify(buildRegistrationBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Could not send verification email.');
        setLoading(false);
        return;
      }
      if (data.cooldown) {
        setError(data.message || 'Please wait before requesting another code.');
        setLoading(false);
        return;
      }
      if (data.verificationRequired === false) {
        await handleCompleteRegistration(null, { skipCode: true });
        return;
      }
      setVerifyNote(
        data.message ||
          'A 6-digit verification code was sent. Check your inbox and Spam folder, then enter the code below.',
      );
      setRegStep('verify');
      setLoading(false);
    } catch {
      setError('Network error. Make sure the backend is running on port 8001.');
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (codeOverride, { skipCode = false } = {}) => {
    const body = buildRegistrationBody();
    if (!skipCode) {
      const code = (codeOverride ?? verificationCode).trim();
      if (!/^\d{6}$/.test(code)) {
        setError('Enter the 6-digit code from your email.');
        setLoading(false);
        return;
      }
      body.verificationCode = code;
    }
    setLoading(true);
    try {
      const res = await apiFetch('/public/register', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Registration failed.');
        setLoading(false);
        return;
      }
      if (data.token) setApiAccessToken(data.token);
      onRegister(data.user || {});
    } catch {
      setError('Network error. Make sure the backend is running on port 8001.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (regStep !== 'verify') return;
    setError('');
    if (!validateRegistrationForm()) return;
    await handleCompleteRegistration();
  };

  const selectedBusinessType = useMemo(
    () => businessTypes.find((t) => t.code === businessType),
    [businessTypes, businessType]
  );

  const handleResendCode = async () => {
    setError('');
    setVerifyNote('');
    if (!validateRegistrationForm()) return;
    setLoading(true);
    try {
      const res = await apiFetch('/public/register/send-verification', {
        method: 'POST',
        body: JSON.stringify(buildRegistrationBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.message || 'Could not resend code.');
      } else if (data.cooldown) {
        setError(data.message || 'Please wait before requesting another code.');
      } else {
        setVerifyNote(data.message || 'A new verification code was sent to your email.');
      }
    } catch {
      setError('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const canSendCode =
    !loading &&
    !typesLoading &&
    !legalLoading &&
    legal &&
    businessTypes.length > 0 &&
    acceptedTerms &&
    branchRegion &&
    branchDistrict &&
    branchName.trim() &&
    email.trim() &&
    password.length >= 8 &&
    password === confirmPassword;

  return (
    <PublicAuthShell
      active="register"
      embedded={embedded}
      showNav
      authTheme
      centerVertically={false}
      onHome={onGoHome}
      onLogin={onShowLogin}
      onRegister={onShowRegister || (() => {})}
    >
      <div className="public-auth-card login-ag-card register-ag-card">
        <form className="login-ag-form register-ag-form" onSubmit={handleSubmit}>
          <header className="login-ag-header">
            <div className="login-ag-logo-wrap auth-animate">
              <img src="/branding/cb-app-logo-circle.svg" alt="" className="login-ag-logo" />
            </div>
            <h1 className="login-ag-brand auth-animate auth-animate--d1">
              cbonline
              <span className="login-ag-brand-sub">app</span>
            </h1>
            <p className="login-ag-subtitle auth-animate auth-animate--d2">{t('auth.createAccountTitle')}</p>
          </header>

          {error ? <div className="error login-ag-error auth-animate--swap">{error}</div> : null}

          <div className="login-ag-field auth-animate auth-animate--d3">
            <label htmlFor="reg-owner" className="login-ag-label">
              {t('auth.fullName')}
            </label>
            <input
              id="reg-owner"
              className="login-ag-input"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Jina lako kamili"
              autoComplete="name"
            />
          </div>

          <div className="login-ag-field auth-animate auth-animate--d4">
            <label htmlFor="reg-company" className="login-ag-label">
              {t('auth.businessName')}
            </label>
            <input
              id="reg-company"
              className="login-ag-input"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Mfano: Duka la Nguo — Kariakoo"
              autoComplete="organization"
            />
          </div>

          <div className="login-ag-field auth-animate auth-animate--d5">
            <label htmlFor="reg-email" className="login-ag-label">
              {t('auth.email')}
            </label>
            <input
              id="reg-email"
              type="email"
              className="login-ag-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>

          <div className="login-ag-field">
            <label htmlFor="reg-btype" className="login-ag-label">
              Business Type <span className="register-ag-required">*</span>
            </label>
            <div className="register-ag-select-wrap">
              <select
                id="reg-btype"
                name="businessType"
                className="login-ag-input register-ag-select"
                value={businessType}
                onChange={(e) => setBusinessType(e.target.value)}
                disabled={typesLoading || businessTypes.length === 0}
                required
              >
                <option value="" disabled>
                  Select your business type
                </option>
                {businessTypes.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {typesLoadNote ? (
              <p className="register-ag-note register-ag-note--warn" role="status">
                {typesLoadNote}
              </p>
            ) : null}
            {selectedBusinessType?.description ? (
              <p className="register-ag-note">{selectedBusinessType.description}</p>
            ) : null}
          </div>

          <div className="login-ag-field">
            <label htmlFor="reg-branch" className="login-ag-label">
              Branch / shop name <span className="register-ag-required">*</span>
            </label>
            <input
              id="reg-branch"
              className="login-ag-input"
              value={branchName}
              onChange={(e) => setBranchName(e.target.value)}
              placeholder="Mfano: Duka Kariakoo, Main"
              required
            />
          </div>

          <div className="register-ag-location-row">
            <div className="login-ag-field">
              <label htmlFor="reg-region" className="login-ag-label">
                Region (Mkoa) <span className="register-ag-required">*</span>
              </label>
              <div className="register-ag-select-wrap">
                <select
                  id="reg-region"
                  className="login-ag-input register-ag-select"
                  value={branchRegion}
                  onChange={(e) => {
                    setBranchRegion(e.target.value);
                    setBranchDistrict('');
                  }}
                  required
                >
                  <option value="">Choose region…</option>
                  {TANZANIA_REGIONS.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="login-ag-field">
              <label htmlFor="reg-district" className="login-ag-label">
                District (Wilaya) <span className="register-ag-required">*</span>
              </label>
              <div className="register-ag-select-wrap">
                <select
                  id="reg-district"
                  className="login-ag-input register-ag-select"
                  value={branchDistrict}
                  onChange={(e) => setBranchDistrict(e.target.value)}
                  disabled={!branchRegion}
                  required
                >
                  <option value="">{branchRegion ? 'Choose district…' : 'Select region first'}</option>
                  {districtOptions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="login-ag-field">
            <label htmlFor="reg-street" className="login-ag-label">
              Street / area (Mtaa) <span className="register-ag-optional">(optional)</span>
            </label>
            <input
              id="reg-street"
              className="login-ag-input"
              value={branchStreet}
              onChange={(e) => setBranchStreet(e.target.value)}
              placeholder="Mfano: Kariakoo, Industrial Area"
            />
          </div>

          <div className="login-ag-field">
            <label htmlFor="reg-phone" className="login-ag-label">
              Business Phone <span className="register-ag-optional">(optional)</span>
            </label>
            <input
              id="reg-phone"
              className="login-ag-input"
              value={companyPhone}
              onChange={(e) => setCompanyPhone(e.target.value)}
              placeholder="0712 345 678"
              autoComplete="tel"
            />
          </div>

          <div className="login-ag-field">
            <label htmlFor="reg-password" className="login-ag-label">
              Password
            </label>
            <div className="login-ag-password-wrap">
              <input
                id="reg-password"
                type={showPassword ? 'text' : 'password'}
                className="login-ag-input login-ag-input--password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password (min 8 characters)"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="login-ag-eye"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <LuEyeOff size={18} aria-hidden /> : <LuEye size={18} aria-hidden />}
              </button>
            </div>
          </div>

          <div className="login-ag-field">
            <label htmlFor="reg-confirm-password" className="login-ag-label">
              Confirm Password
            </label>
            <div className="login-ag-password-wrap">
              <input
                id="reg-confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                className="login-ag-input login-ag-input--password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="login-ag-eye"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <LuEyeOff size={18} aria-hidden /> : <LuEye size={18} aria-hidden />}
              </button>
            </div>
          </div>

          <label className="register-ag-legal">
            <input
              type="checkbox"
              className="register-ag-legal__check"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              disabled={legalLoading || !legal}
            />
            <span className="register-ag-legal__text">
              I agree to the{' '}
              <button
                type="button"
                className="login-ag-link"
                onClick={onShowTerms}
                disabled={!legal?.terms || typeof onShowTerms !== 'function'}
              >
                Terms
              </button>{' '}
              and{' '}
              <button
                type="button"
                className="login-ag-link"
                onClick={onShowPrivacy}
                disabled={!legal?.privacy || typeof onShowPrivacy !== 'function'}
              >
                Privacy Policy
              </button>
              <span className="register-ag-required"> *</span>
            </span>
          </label>
          {legalLoadNote ? (
            <p className="register-ag-note register-ag-note--warn" role="status">
              {legalLoadNote}
            </p>
          ) : null}

          {regStep === 'form' ? (
            <button
              type="button"
              className="login-ag-submit register-ag-send-code"
              disabled={!canSendCode}
              onClick={() => void handleSendVerification()}
            >
              {loading ? <span className="login-ag-spinner" aria-hidden /> : t('auth.sendCode')}
            </button>
          ) : null}

          {regStep === 'verify' ? (
            <section className="register-ag-verify-panel" aria-live="polite">
              {verifyNote ? (
                <p className="register-ag-verify-banner" role="status">
                  {verifyNote}
                </p>
              ) : null}

              <div className="login-ag-field">
                <label htmlFor="reg-verify-code" className="login-ag-label">
                  Verification code
                </label>
                <input
                  id="reg-verify-code"
                  className="login-ag-input register-ag-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  disabled={loading}
                  autoFocus
                />
                <p className="register-ag-note">Enter the 6-digit code from your email to complete registration.</p>
              </div>

              <div className="register-ag-inline-actions">
                <button
                  type="button"
                  className="login-ag-link"
                  onClick={() => {
                    setRegStep('form');
                    setVerificationCode('');
                    setVerifyNote('');
                    setError('');
                  }}
                >
                  Edit details
                </button>
                <button
                  type="button"
                  className="login-ag-link"
                  onClick={() => void handleResendCode()}
                  disabled={loading}
                >
                  Resend code
                </button>
              </div>

              <button
                type="submit"
                className="login-ag-submit register-ag-submit"
                disabled={
                  loading ||
                  verificationCode.length !== 6 ||
                  typesLoading ||
                  legalLoading ||
                  !legal ||
                  !acceptedTerms
                }
              >
                {loading ? <span className="login-ag-spinner" aria-hidden /> : 'Verify code & create account'}
              </button>
            </section>
          ) : null}

          {!hideSwitch ? (
            <p className="login-ag-signup register-ag-signup auth-animate auth-animate--d6">
              {t('auth.haveAccount')}{' '}
              <a
                href="#sign-in"
                className="login-ag-signup__link"
                onClick={(e) => {
                  e.preventDefault();
                  onShowLogin();
                }}
              >
                {t('auth.signIn')}
              </a>
            </p>
          ) : null}
        </form>
      </div>
    </PublicAuthShell>
  );
};

export default RegisterPage;
